import React, { useState } from 'react';
import { X, TrendingUp, TrendingDown, DollarSign, Zap, BarChart2 } from 'lucide-react';
import { LocationData, DailySnapshot } from '../types';
import { SERVER_TYPES, TRANSIT_PROVIDERS, IXP_EXCHANGES, NAWAS_TIERS, POWER_SUPPLIERS, RENEWABLE_TYPES, SOLAR_PROFILE, WIND_PROFILE, LOCATION_RENEWABLE_FACTORS } from '../constants';
import { calculatePowerCosts, calculateCoolingCosts, calculateMaintenanceCosts } from '../utils/costCalculations';
import { POWER_GRIDS } from '../constants';
import ServerEfficiencyDashboard from './ServerEfficiencyDashboard';

interface FinancialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  locations: Record<string, LocationData>;
  formatCurrency: (amount: number, decimals?: number) => string;
  currentHour: number;
  reputation?: number;
  unlockLevel?: number;
  powerEfficiencyUpgrade?: number;
  coolingEfficiencyUpgrade?: number;
  dailySnapshots?: DailySnapshot[];
}

function getLocationConnectivity(location: LocationData): number {
  if (!location.mmr?.built) return 0;
  const transitGbps = (location.mmr.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0);
  const ixpGbps = (location.mmr.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);
  const totalProvisionedGbps = transitGbps + ixpGbps;
  if (totalProvisionedGbps === 0) return 0;
  let totalDemandGbps = 0;
  Object.values(location.floors ?? {}).forEach(f =>
    f.racks?.forEach(r =>
      r.servers?.forEach(s => { totalDemandGbps += (s.utilizationRate ?? 1.0); })
    )
  );
  if (totalDemandGbps === 0) return 1;
  return Math.min(1, totalProvisionedGbps / totalDemandGbps);
}

function getLocationRevenue(location: LocationData, reputation: number): number {
  const repMult = reputation / 100;
  const connectivity = getLocationConnectivity(location);
  let rev = 0;
  Object.values(location.floors ?? {}).forEach(floor => {
    floor.racks?.forEach(rack => {
      rack.servers?.forEach(server => {
        const isDown = server.failureStatus === 'failed' || server.failureStatus === 'repairing';
        if (isDown) return;
        const satisfaction = server.satisfactionScore ?? 100;
        const satisfactionMult = 1 - Math.max(0, 100 - satisfaction) * 0.005;
        rev += server.revenue
          * (server.utilizationRate ?? 1.0)
          * (server.ageEfficiencyMultiplier ?? 1.0)
          * repMult
          * satisfactionMult
          * connectivity;
      });
    });
  });
  return rev * location.businessMultiplier;
}

function getLocationTransitCost(location: LocationData): number {
  if (!location.mmr?.built) return 0;
  const transitProviders = location.mmr.transitProviders ?? [];
  const ixpConnections = location.mmr.ixpConnections ?? [];
  const totalTransitGbps = transitProviders.reduce((s, tp) => s + tp.committedGbps, 0);
  const totalIxpGbps = ixpConnections.reduce((s, c) => s + c.portSpeedGbps, 0);
  const peeringOk = totalTransitGbps > 0 && (totalIxpGbps / (totalIxpGbps + totalTransitGbps)) >= 0.70;
  const transitDiscount = peeringOk ? 0.85 : 1.0;
  let cost = 0;
  transitProviders.forEach(tp => {
    const p = TRANSIT_PROVIDERS.find(x => x.id === tp.providerId);
    if (p) cost += (p.costPerGbpsMonth * tp.committedGbps * transitDiscount) / 30;
  });
  ixpConnections.forEach(c => {
    const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
    const port = ixp?.ports.find(p => p.speedGbps === c.portSpeedGbps);
    if (port) cost += port.monthlyCost / 30;
  });
  if (location.nawasSubscription) {
    const tier = NAWAS_TIERS.find(t => t.id === location.nawasSubscription!.tierId);
    if (tier) cost += tier.monthlyCost / 30;
  }
  return cost;
}

function getLocationExportRevenue(location: LocationData, powerGrids: Record<string, any>, locationId?: string): number {
  const renewables = location.renewableInstallations ?? [];
  if (renewables.length === 0) return 0;
  const bat = location.batteryStorage?.installed ? location.batteryStorage : null;
  const feedInFraction = bat?.exportFeedInFraction ?? 0.6;
  const chargeRateKW = bat?.chargeRateKW ?? 0;
  const batteryCapacity = bat?.capacityKWh ?? 0;

  const grid = POWER_GRIDS[location.powerGridId ?? ''];
  const supplier = grid?.suppliers?.find((s: any) => s.id === location.selectedPowerSupplierId);
  if (!supplier) return 0;

  const dcDrawKW = Object.values(location.floors ?? {}).reduce((acc, floor) =>
    acc + floor.racks.reduce((ra, rack) =>
      ra + rack.servers.reduce((sa, s) => sa + s.powerDraw, 0), 0), 0);

  let totalExportRevenue = 0;
  let simBatteryCharge = bat ? bat.chargeLevel : 0;

  const locFactors = LOCATION_RENEWABLE_FACTORS[locationId ?? ''] ?? { solar: 1, wind: 1 };
  for (let h = 0; h < 24; h++) {
    let renewableKW = 0;
    renewables.forEach(inst => {
      const rt = RENEWABLE_TYPES.find(r => r.id === inst.sourceType);
      if (!rt) return;
      const profile = inst.sourceType === 'solar' ? SOLAR_PROFILE : WIND_PROFILE;
      const factor = inst.sourceType === 'solar' ? locFactors.solar : locFactors.wind;
      renewableKW += rt.capacityKW * inst.units * profile[h] * factor;
    });

    const surplus = Math.max(0, renewableKW - dcDrawKW);
    let batteryAbsorb = 0;
    if (surplus > 0 && bat) {
      batteryAbsorb = Math.min(surplus, chargeRateKW, batteryCapacity - simBatteryCharge);
      simBatteryCharge = Math.min(batteryCapacity, simBatteryCharge + batteryAbsorb);
    } else if (bat && simBatteryCharge > 0) {
      const deficit = Math.max(0, dcDrawKW - renewableKW);
      const discharge = Math.min(deficit, chargeRateKW, simBatteryCharge);
      simBatteryCharge -= discharge;
    }
    const exportKWh = Math.max(0, surplus - batteryAbsorb);
    if (exportKWh === 0) continue;

    let rate = supplier.costPerKWh;
    const schedule = supplier.tariffSchedule as Array<{ startHour: number; endHour: number; rate: number }> | undefined;
    if (schedule) {
      for (const win of schedule) {
        const inWindow = win.startHour < win.endHour
          ? h >= win.startHour && h < win.endHour
          : h >= win.startHour || h < win.endHour;
        if (inWindow) { rate = win.rate; break; }
      }
    }
    totalExportRevenue += exportKWh * rate * feedInFraction;
  }
  return totalExportRevenue;
}

function getServerCount(location: LocationData): number {
  let n = 0;
  Object.values(location.floors ?? {}).forEach(f => f.racks?.forEach(r => { n += r.servers?.length ?? 0; }));
  return n;
}

function getTotalTrafficGbps(location: LocationData): number {
  let gbps = 0;
  Object.values(location.floors ?? {}).forEach(f => {
    f.racks?.forEach(r => {
      r.servers?.forEach(s => {
        if (s.failureStatus === 'failed') return;
        const st = SERVER_TYPES.find(t => t.id === s.type);
        if (st) gbps += st.trafficGbps * (s.utilizationRate ?? 1.0);
      });
    });
  });
  return gbps;
}

function TrendChart({ snapshots, formatCurrency }: { snapshots: DailySnapshot[]; formatCurrency: (n: number) => string }) {
  const W = 640, H = 220, PAD = { top: 16, right: 16, bottom: 32, left: 72 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const allValues = snapshots.flatMap(s => [s.revenue, s.costs, s.net]);
  const minVal = Math.min(0, ...allValues);
  const maxVal = Math.max(1, ...allValues);
  const range = maxVal - minVal || 1;

  const xOf = (i: number) => PAD.left + (i / (snapshots.length - 1)) * chartW;
  const yOf = (v: number) => PAD.top + chartH - ((v - minVal) / range) * chartH;

  const line = (key: 'revenue' | 'costs' | 'net') =>
    snapshots.map((s, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(s[key]).toFixed(1)}`).join(' ');

  const zeroY = yOf(0);

  // Y-axis ticks
  const ticks = 5;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => minVal + (range * i) / ticks);

  // X-axis labels (first, middle, last)
  const xLabels = [0, Math.floor((snapshots.length - 1) / 2), snapshots.length - 1].filter((v, i, a) => a.indexOf(v) === i);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded" /> Revenue</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-red-400 inline-block rounded" /> Costs</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-violet-400 inline-block rounded" /> Net</span>
        <span className="ml-auto text-gray-600">{snapshots.length} days recorded (max 30)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: 220 }}>
        {/* Grid lines */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={yOf(v)} y2={yOf(v)}
              stroke="#374151" strokeWidth="1" strokeDasharray={v === 0 ? '0' : '4 3'} />
            <text x={PAD.left - 6} y={yOf(v)} textAnchor="end" dominantBaseline="middle"
              fill="#6b7280" fontSize="10">
              {formatCurrency(v)}
            </text>
          </g>
        ))}
        {/* Zero line highlight */}
        {minVal < 0 && (
          <line x1={PAD.left} x2={W - PAD.right} y1={zeroY} y2={zeroY}
            stroke="#6b7280" strokeWidth="1.5" />
        )}
        {/* X axis labels */}
        {xLabels.map(i => (
          <text key={i} x={xOf(i)} y={H - 6} textAnchor="middle" fill="#6b7280" fontSize="10">
            Day {snapshots[i].day}
          </text>
        ))}
        {/* Lines */}
        <path d={line('costs')} fill="none" stroke="#f87171" strokeWidth="1.5" />
        <path d={line('revenue')} fill="none" stroke="#34d399" strokeWidth="2" />
        <path d={line('net')} fill="none" stroke="#a78bfa" strokeWidth="2" strokeDasharray="6 3" />
        {/* Net area fill */}
        {snapshots.length >= 2 && (
          <path
            d={`${line('net')} L${xOf(snapshots.length - 1).toFixed(1)},${zeroY} L${xOf(0).toFixed(1)},${zeroY} Z`}
            fill="url(#netFill)" opacity="0.15"
          />
        )}
        <defs>
          <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {/* Summary stats */}
      {snapshots.length >= 2 && (() => {
        const last7 = snapshots.slice(-7);
        const avgRev = last7.reduce((s, d) => s + d.revenue, 0) / last7.length;
        const avgNet = last7.reduce((s, d) => s + d.net, 0) / last7.length;
        const firstSnap = snapshots[0];
        const lastSnap = snapshots[snapshots.length - 1];
        const netGrowth = lastSnap.net - firstSnap.net;
        return (
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Avg Revenue (last 7d)</div>
              <div className="text-emerald-400 font-bold">{formatCurrency(avgRev)}/day</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Avg Net (last 7d)</div>
              <div className={`font-bold ${avgNet >= 0 ? 'text-violet-400' : 'text-red-400'}`}>{formatCurrency(avgNet)}/day</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3">
              <div className="text-gray-400 text-xs mb-1">Net trend ({snapshots.length}d)</div>
              <div className={`font-bold ${netGrowth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {netGrowth >= 0 ? '↑ ' : '↓ '}{formatCurrency(Math.abs(netGrowth))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const FinancialsModal: React.FC<FinancialsModalProps> = ({
  isOpen, onClose, locations, formatCurrency, currentHour, reputation = 100,
  unlockLevel = 0, powerEfficiencyUpgrade = 0, coolingEfficiencyUpgrade = 0, dailySnapshots = [],
}) => {
  const [activeTab, setActiveTab] = useState<'financial' | 'trend' | 'efficiency'>('financial');
  if (!isOpen) return null;

  const costUpgrades = { powerEfficiency: powerEfficiencyUpgrade, coolingEfficiency: coolingEfficiencyUpgrade };
  const locEntries = Object.entries(locations).filter(([, l]) => l.unlocked);

  const rows = locEntries.map(([locId, location]) => {
    const revenue = getLocationRevenue(location, reputation);
    const powerCost = calculatePowerCosts({ [locId]: location }, POWER_GRIDS, currentHour, undefined, costUpgrades);
    const coolingCost = calculateCoolingCosts({ [locId]: location }, POWER_GRIDS, undefined, costUpgrades);
    const maintenanceCost = calculateMaintenanceCosts({ [locId]: location });
    const transitCost = getLocationTransitCost(location);
    const exportRevenue = getLocationExportRevenue(location, POWER_GRIDS, locId);
    const totalCosts = powerCost + coolingCost + maintenanceCost + transitCost;
    const net = revenue + exportRevenue - totalCosts;
    const serverCount = getServerCount(location);
    const trafficGbps = getTotalTrafficGbps(location);
    const transitGbps = (location.mmr?.transitProviders ?? []).reduce((s, tp) => s + tp.committedGbps, 0)
      + (location.mmr?.ixpConnections ?? []).reduce((s, c) => s + c.portSpeedGbps, 0);

    return { locId, location, revenue, exportRevenue, powerCost, coolingCost, maintenanceCost, transitCost, totalCosts, net, serverCount, trafficGbps, transitGbps };
  });

  const totals = rows.reduce((acc, r) => ({
    revenue: acc.revenue + r.revenue,
    powerCost: acc.powerCost + r.powerCost,
    coolingCost: acc.coolingCost + r.coolingCost,
    maintenanceCost: acc.maintenanceCost + r.maintenanceCost,
    transitCost: acc.transitCost + r.transitCost,
    exportRevenue: acc.exportRevenue + r.exportRevenue,
    totalCosts: acc.totalCosts + r.totalCosts,
    net: acc.net + r.net,
  }), { revenue: 0, exportRevenue: 0, powerCost: 0, coolingCost: 0, maintenanceCost: 0, transitCost: 0, totalCosts: 0, net: 0 });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <DollarSign size={18} className="text-emerald-400" />
              <h2 className="text-lg font-bold">Financials</h2>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setActiveTab('financial')}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${activeTab === 'financial' ? 'bg-emerald-700 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                Breakdown
              </button>
              <button
                onClick={() => setActiveTab('trend')}
                className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors ${activeTab === 'trend' ? 'bg-violet-700 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <BarChart2 size={11} /> 30-Day Trend
              </button>
              <button
                onClick={() => setActiveTab('efficiency')}
                className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors ${activeTab === 'efficiency' ? 'bg-blue-700 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <Zap size={11} /> Efficiency
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {activeTab === 'trend' && (
          <div className="flex-1 overflow-auto p-5">
            {dailySnapshots.length < 2 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500 gap-2">
                <BarChart2 size={32} className="opacity-40" />
                <p className="text-sm">Trend data builds up after your first in-game day.</p>
                <p className="text-xs text-gray-600">Come back tomorrow!</p>
              </div>
            ) : (
              <TrendChart snapshots={dailySnapshots} formatCurrency={formatCurrency} />
            )}
          </div>
        )}

        {activeTab === 'efficiency' && (
          <div className="flex-1 overflow-auto p-5">
            <ServerEfficiencyDashboard
              serverTypes={SERVER_TYPES}
              formatCurrency={formatCurrency}
              unlockLevel={unlockLevel}
              reputation={reputation}
              powerEfficiencyUpgrade={powerEfficiencyUpgrade}
            />
          </div>
        )}

        {activeTab === 'financial' && <div className="flex-1 overflow-auto p-5 space-y-6">

          {/* Per-location table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-700">
                  <th className="text-left pb-2 pr-4">Location</th>
                  <th className="text-right pb-2 px-3">Servers</th>
                  <th className="text-right pb-2 px-3">Server Rev</th>
                  <th className="text-right pb-2 px-3">Grid Export</th>
                  <th className="text-right pb-2 px-3">Power</th>
                  <th className="text-right pb-2 px-3">Cooling</th>
                  <th className="text-right pb-2 px-3">Maint.</th>
                  <th className="text-right pb-2 px-3">Transit</th>
                  <th className="text-right pb-2 pl-3 font-semibold">Net/day</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {rows.map(r => (
                  <tr key={r.locId} className="hover:bg-gray-800/40">
                    <td className="py-3 pr-4">
                      <div className="font-medium">{r.location.name}</div>
                      <div className="text-xs text-gray-500">
                        {r.serverCount} servers · {r.trafficGbps.toFixed(1)} Gbps traffic · {r.transitGbps}G transit
                      </div>
                    </td>
                    <td className="text-right px-3 text-gray-300">{r.serverCount}</td>
                    <td className="text-right px-3 text-emerald-400">{formatCurrency(r.revenue)}</td>
                    <td className="text-right px-3 text-green-400">{r.exportRevenue > 0 ? `+${formatCurrency(r.exportRevenue)}` : <span className="text-gray-600">—</span>}</td>
                    <td className="text-right px-3 text-red-400">−{formatCurrency(r.powerCost)}</td>
                    <td className="text-right px-3 text-blue-400">−{formatCurrency(r.coolingCost)}</td>
                    <td className="text-right px-3 text-orange-400">−{formatCurrency(r.maintenanceCost)}</td>
                    <td className="text-right px-3 text-violet-400">−{formatCurrency(r.transitCost)}</td>
                    <td className={`text-right pl-3 font-bold ${r.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {r.net >= 0 ? '+' : ''}{formatCurrency(r.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-600">
                <tr className="font-semibold">
                  <td className="pt-3 pr-4 text-gray-400">Total</td>
                  <td className="text-right px-3 text-gray-300">{rows.reduce((s, r) => s + r.serverCount, 0)}</td>
                  <td className="text-right px-3 text-emerald-400">{formatCurrency(totals.revenue)}</td>
                  <td className="text-right px-3 text-green-400">{totals.exportRevenue > 0 ? `+${formatCurrency(totals.exportRevenue)}` : <span className="text-gray-600">—</span>}</td>
                  <td className="text-right px-3 text-red-400">−{formatCurrency(totals.powerCost)}</td>
                  <td className="text-right px-3 text-blue-400">−{formatCurrency(totals.coolingCost)}</td>
                  <td className="text-right px-3 text-orange-400">−{formatCurrency(totals.maintenanceCost)}</td>
                  <td className="text-right px-3 text-violet-400">−{formatCurrency(totals.transitCost)}</td>
                  <td className={`text-right pl-3 font-bold text-lg ${totals.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totals.net >= 0 ? '+' : ''}{formatCurrency(totals.net)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Per-location cost breakdown bars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map(r => {
              const items = [
                { label: 'Power', value: r.powerCost, color: 'bg-red-500' },
                { label: 'Cooling', value: r.coolingCost, color: 'bg-blue-500' },
                { label: 'Maintenance', value: r.maintenanceCost, color: 'bg-orange-500' },
                { label: 'Transit', value: r.transitCost, color: 'bg-violet-500' },
              ].filter(i => i.value > 0);
              const margin = r.revenue > 0 ? Math.round((r.net / r.revenue) * 100) : 0;
              return (
                <div key={r.locId} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold">{r.location.name}</span>
                    <div className="flex items-center gap-1.5">
                      {r.net >= 0
                        ? <TrendingUp size={14} className="text-emerald-400" />
                        : <TrendingDown size={14} className="text-red-400" />}
                      <span className={`text-sm font-bold ${r.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {margin}% margin
                      </span>
                    </div>
                  </div>
                  {/* Stacked cost bar */}
                  {r.revenue > 0 && (
                    <div className="mb-3">
                      <div className="flex h-3 rounded overflow-hidden bg-gray-700 gap-px">
                        {items.map(item => (
                          <div
                            key={item.label}
                            className={item.color}
                            style={{ width: `${Math.min(100, (item.value / r.revenue) * 100)}%` }}
                            title={`${item.label}: ${formatCurrency(item.value)}/day`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-3 mt-1.5 flex-wrap">
                        {items.map(item => (
                          <span key={item.label} className="text-xs text-gray-400">
                            <span className={`inline-block w-2 h-2 rounded-sm mr-1 ${item.color}`} />
                            {item.label} {Math.round((item.value / r.revenue) * 100)}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    Server {formatCurrency(r.revenue)}/day · Costs {formatCurrency(r.totalCosts)}/day
                  </div>
                </div>
              );
            })}
          </div>
        </div>}
      </div>
    </div>
  );
};

export default FinancialsModal;
